import { useState, useCallback, useEffect, useMemo } from 'react';
import { Box, IconButton, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, NewPageTitleBar, DeleteConfirmationDialog } from '@/components';
import EditorSidebar from '@/components/EditorSidebar';
import NewWcnTable, { DataTableColumn } from '@/components/datatable/NewWcnTable.tsx';
import AuthenticationProviderEditor from './components/authenticationproviders/AuthenticationProviderEditor';
import { useNotifications } from '@core/hooks/useNotifications';
import { getMenuData } from '@core/data/menuData';
import { FCSecurePage, useRegisterRefresh } from '@/components/shared/navigation/securePage.ts';
import { AuthenticationProvider, incidentsService } from '@/services';
import { DateTime } from 'luxon';

const AuthenticationProviders: FCSecurePage = () => {
    const { t } = useTranslation();
    const { notifySuccess, notifyErrorResponse } = useNotifications();

    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [authenticationProviders, setAuthenticationProviders] = useState<AuthenticationProvider[]>([]);
    const [selected, setSelected] = useState<AuthenticationProvider | null>(null);
    const [itemToDelete, setItemToDelete] = useState<AuthenticationProvider | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);

    // Load authentication providers
    const load = async () => {
        setLoading(true);
        try {
            const data = await incidentsService.getAuthenticationProviders();
            setAuthenticationProviders(data || []);
            setLastUpdate(DateTime.now());
        } catch (error) {
            console.error('Failed to load authentication providers:', error);
            notifyErrorResponse(error);
        } finally {
            setLoading(false);
        }
    };

    useRegisterRefresh(load, loading);

    // Add new authentication provider
    const handleAdd = () => {
        const newProvider: AuthenticationProvider = {
            id: undefined,
            name: '',
            authenticationType: '',
            url: '',
            token: '',
            authorizationHeader: '',
            customProperties: []
        };
        setSelected(newProvider);
    };

    // Save authentication provider
    const saveItem = useCallback(
        async (item: AuthenticationProvider) => {
            if (isSaving) return;
            setIsSaving(true);

            try {
                if (item?.id) {
                    await incidentsService.updateAuthenticationProvider(item.id, item);
                } else {
                    await incidentsService.createAuthenticationProvider(item);
                }

                notifySuccess();
                setSelected(null);
                load();
            } catch (error) {
                console.error('Failed to save authentication provider:', error);
                notifyErrorResponse(error);
            } finally {
                setIsSaving(false);
            }
        },
        [isSaving]
    );

    // Select authentication provider
    const select = (item: AuthenticationProvider) => {
        const selectedItem = {
            ...item,
            customProperties: item.customProperties || []
        };
        setSelected(selectedItem);
    };

    // Ask for delete confirmation
    const askDelete = (item: AuthenticationProvider) => {
        setItemToDelete(item);
        setDeleteDialogOpen(true);
    };

    // Delete authentication provider
    const deleteItem = async () => {
        if (!itemToDelete?.id) return;

        try {
            await incidentsService.deleteAuthenticationProvider(itemToDelete.id);
            notifySuccess();
            setDeleteDialogOpen(false);
            setItemToDelete(null);
            setSelected(null);
            load();
        } catch (error) {
            console.error('Failed to delete authentication provider:', error);
            notifyErrorResponse(error);
        }
    };

    // Table columns configuration
    const columns: DataTableColumn<AuthenticationProvider>[] = useMemo(
        () => [
            {
                text: t('name'),
                value: 'name',
                width: 300
            },
            {
                text: t('type'),
                value: 'authenticationType',
                width: 200,
                render: (provider) => {
                    const cleanType = provider.authenticationType?.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
                    return cleanType ? t(cleanType) : '';
                }
            },
            {
                text: t('url'),
                value: 'url',
                width: 500
            },
            {
                text: t('token'),
                value: 'token',
                width: 600
            },
            {
                text: t('authorizationHeader'),
                value: 'authorizationHeader',
                width: 300
            },
            {
                text: t('actions'),
                value: 'actions',
                width: 100,
                sortable: false,
                isAction: true,
                render: (provider) => (
                    <Box className="table-actions">
                        <IconButton size="small" onClick={() => select(provider)}>
                            <i className="bi bi-pencil" />
                        </IconButton>
                        <IconButton size="small" onClick={() => askDelete(provider)}>
                            <i className="bi bi-trash3" />
                        </IconButton>
                    </Box>
                )
            }
        ],
        [t]
    );

    const renderContent = () => {
        if (loading) {
            return <LoadingSpinner showBackground={false} />;
        }

        return (
            <NewWcnTable
                columns={columns}
                data={authenticationProviders}
                loading={loading}
                noDataMessage={t('noData')}
                size="small"
            />
        );
    };

    // Load data on mount
    useEffect(() => {
        load();
    }, []);

    return (
        <Box className="authentication-providers-page">
            <NewPageTitleBar
                icon={<i className="zmdi zmdi-settings" />}
                title={t('authenticationProviders')}
                menus={getMenuData()}
                iconSize="20px"
                currentRoute={{ path: '/manage-organisation/authenticationProviders' }}
                t={t}
                lastUpdate={lastUpdate}
            />

            <Box sx={{ width: '100%', px: 4, pb: 1, '@media (max-width: 480px)': { pt: 3 } }}>
                <Button
                    variant="text"
                    onClick={handleAdd}
                    disabled={loading}
                    className="pagetitlebar-action-btn pagetitlebar-action-btn--default"
                >
                    <Box className="pagetitlebar-action-btn-icon">
                        <i
                            className="bi bi-plus-lg"
                            style={{ fontSize: '16px', color: 'inherit', lineHeight: 1, paddingRight: 6 }}
                        />
                    </Box>
                    <span>{t('add')}</span>
                </Button>
            </Box>

            <Box className="page-content" sx={{ mt: 2 }}>
                {renderContent()}
            </Box>

            <EditorSidebar open={!!selected} onClose={() => setSelected(null)}>
                {selected && (
                    <AuthenticationProviderEditor
                        item={selected}
                        isSaving={isSaving}
                        onSave={saveItem}
                        onDelete={askDelete}
                        onCancel={() => setSelected(null)}
                    />
                )}
            </EditorSidebar>

            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                heading={t('deleteHeading')}
                message={t('deleteMessage', { item: t('authenticationProvider') })}
                onConfirm={deleteItem}
                onCancel={() => {
                    setDeleteDialogOpen(false);
                    setItemToDelete(null);
                }}
            />
        </Box>
    );
};

export default AuthenticationProviders;
